import classNames from 'classnames';
import { useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';

import { trackGAEvent } from '@/utility/analytics';
import { ACTIVITIY_EVENTS } from '@/utility/analyticsConsts';
import { timeSince } from '@/utility/dateHelper';
import { t } from '@/utility/localization';
import {
  CM_PORTAL_MEMBERS_PAGE_ROUTE,
  getChallengeDetailRoute,
  getCmpFolderPageRoute
} from '@/utility/routesHelper';

import { get1on1SessionManagementUrl } from '@/features/1on1Sessions/utils';

import { convertObjectToQueryString } from '@/services/helpers/queryString';

import Markdown from '@/components/common/Markdown';
import NextImage from '@/components/common/NextImage';
import Icon from '@/components/npl/Icon';

import { getEventManagementUrlPage } from '@/pages/portal/utils/events';

import getConstants from '../constants';

function ActivityRow({ activity }) {
  const router = useRouter();
  const [activityFields, setActivityFields] = useState('');
  const { linkTypes, milestoneKey, activityTypes } = getConstants();
  const activityImageProps = {
    src: activity?.image,
    layout: 'fixed',
    width: 32,
    height: 32
  };

  const isMilestoneActivity = activity?.key?.includes(milestoneKey);
  const isGiftMilestoneActivity =
    isMilestoneActivity && activity?.key?.includes('gift');

  const activityValueArray = activity?.value;

  const getCTALink = (element) => {
    const entityObjectId = element?.metadata?.entityObjectId;
    switch (element?.linkType) {
      // EVENT REGISTRATION
      case linkTypes.EVENT:
        const eventId = entityObjectId;
        const memberEmail = element?.metadata?.email;
        const eventQueryStr = convertObjectToQueryString({
          search: memberEmail
        });
        return `${getEventManagementUrlPage({
          id: eventId
        })}?${eventQueryStr}`;
      // MEMBER
      case linkTypes.MEMBER:
        const email = element?.metadata?.email;
        const memberQueryStr = convertObjectToQueryString({
          searchString: email,
          openSideDrawer: true
        });
        return `${CM_PORTAL_MEMBERS_PAGE_ROUTE}?${memberQueryStr}`;
      // CONTENT/FOLDER PURCHASE
      case linkTypes.FOLDER:
        const folderId = entityObjectId;
        return getCmpFolderPageRoute({ folderId });
      // SESSION PURCHASE
      case linkTypes.SESSION:
        const sessionMemberEmail = element?.metadata?.email;
        const sessionMemberQueryStr = convertObjectToQueryString({
          search: sessionMemberEmail,
          openSideDrawer: true
        });
        return `${get1on1SessionManagementUrl({
          id: entityObjectId
        })}?${sessionMemberQueryStr}`;
      // CHALLENGE REGISTRATION
      case linkTypes.CHALLENGE:
        return `${getChallengeDetailRoute(
          entityObjectId
        )}?tab=participants`;
    }
  };

  useEffect(() => {
    const updates = {};
    activityValueArray?.forEach((element) => {
      const key = element?.key;
      let title = element?.value;

      const link = getCTALink(element);

      if (link) {
        updates[`${key}Link`] = link;
      }

      updates[key] = title;
    });
    setActivityFields((prev) => ({
      ...prev,
      ...updates
    }));
  }, []);

  const primaryValue = activityValueArray?.find(
    (item) => item.isPrimaryLink === true
  );

  const primaryLink = getCTALink(primaryValue) || '';

  const fromNowTime = timeSince(new Date(activity?.activityCreatedAt));

  const trackActivityRowClick = () => {
    switch (activity?.key) {
      case activityTypes.ACTIVITY_SUBSCRIPTION_JOINED:
        trackGAEvent(ACTIVITIY_EVENTS.SUBSCRIPTION_JOINED);
        break;
      case activityTypes.ACTIVITY_SUBSCRIPTION_PENDING_APPROVAL:
        trackGAEvent(ACTIVITIY_EVENTS.SUBSCRIPTION_PENDING_APPROVAL);
        break;
      case activityTypes.ACTIVITY_SUBSCRIPTION_UNSUBSCRIBED:
        trackGAEvent(ACTIVITIY_EVENTS.SUBSCRIPTION_UNSUBSCRIBED);
        break;
      case activityTypes.ACTIVITY_EVENT_REGISTERED:
        trackGAEvent(ACTIVITIY_EVENTS.EVENT_REGISTERED);
        break;
      case activityTypes.ACTIVITY_FOLDER_PURCHASED:
        trackGAEvent(ACTIVITIY_EVENTS.FOLDER_PURCHASED);
        break;
      case activityTypes.ACTIVITY_MILESTONE_COMMUNITY_CREATED:
        trackGAEvent(ACTIVITIY_EVENTS.MILESTONE_COMMUNITY_CREATED);
        break;
      case activityTypes.ACTIVITY_MILESTONE_FIRST_SALE:
        trackGAEvent(ACTIVITIY_EVENTS.MILESTONE_FIRST_SALE);
        break;
      case activityTypes.ACTIVITY_MILESTONE_FIRST_EVENT:
        trackGAEvent(ACTIVITIY_EVENTS.MILESTONE_FIRST_EVENT);
        break;
      case activityTypes.ACTIVITY_MILESTONE_FIRST_CONTENT:
        trackGAEvent(ACTIVITIY_EVENTS.MILESTONE_FIRST_CONTENT);
        break;
      case activityTypes.ACTIVITY_MILESTONE_FIRST_POST:
        trackGAEvent(ACTIVITIY_EVENTS.MILESTONE_FIRST_POST);
        break;
      case activityTypes.ACTIVITY_MILESTONE_ONE_DOLLAR_GIFT:
        trackGAEvent(ACTIVITIY_EVENTS.ACTIVITY_MILESTONE_ONE_DOLLAR_GIFT);
        break;
      case activityTypes.MILESTONE_FIVE_DOLLAR_GIFT:
        trackGAEvent(ACTIVITIY_EVENTS.MILESTONE_FIVE_DOLLAR_GIFT);
        break;
    }
  };

  const handleActivityRowClick = () => {
    // Tracking events
    trackActivityRowClick();
    if (primaryLink) {
      router.push(primaryLink);
    }
  };

  const handleInnerClick = (e) => {
    e.stopPropagation();
  };

  return (
    <>
      <div
        onClick={handleActivityRowClick}
        className={classNames(
          'flex items-center justify-between gap-12 rounded-8 px-8 py-12 bg-blend-multiply hover:bg-npl-neutral-light-solid-2 active:bg-npl-neutral-light-solid-3',
          primaryLink ? 'hover:cursor-pointer' : 'hover:cursor-default'
        )}>
        <span className="flex gap-12 md:items-center">
          {isMilestoneActivity ? (
            isGiftMilestoneActivity ? (
              <div className="flex h-32 w-32 items-center justify-center rounded-8 bg-npl-pink-light-3">
                <Icon
                  name="gift-01"
                  fill="#CD1D8D"
                  width={13}
                  height={13}
                />
              </div>
            ) : (
              <div className="flex h-32 w-32 items-center justify-center rounded-8 bg-npl-yellow-light-solid-3">
                <Icon
                  name="trophy-01"
                  fill="#946800"
                  width={13}
                  height={13}
                />
              </div>
            )
          ) : (
            <div className="min-w-[32px]">
              <NextImage
                className="rounded-full object-cover"
                mobileImgProps={activityImageProps}
              />
            </div>
          )}
          <div>
            <div onClick={handleInnerClick}>
              <Markdown className="text-label-md">
                {t(activity?.key, activityFields)}
              </Markdown>
            </div>

            <div className="mt-12 text-label-sm text-npl-text-icon-on-light-surface-tertiary/50 md:hidden">
              {fromNowTime}
            </div>
          </div>
        </span>
        <div className="hidden text-label-md text-npl-text-icon-on-light-surface-tertiary/50 md:block">
          {fromNowTime}
        </div>
      </div>
    </>
  );
}

export default React.memo(ActivityRow);
